import {
  getGlobalDisplayName,
  getGlobalHitListItemContent,
  type FacilityHit,
  type PersonHit
} from "@/components/AlgoliaSearch";
import { formatLocationText } from "@/components/AlgoliaSearch/utils";
import type { ListItemProps } from "@/components/ListItem/ListItem";
import { matchDisplayNameIndices } from "@/lib/globalSearchMatch";
import { formatPersonSecondaryLine } from "@/lib/personSecondaryLine";

export const GLOBAL_SEARCH_MATCH_CLASS = "font-semibold text-sys-brand-on-surface";
export const GLOBAL_SEARCH_UNMATCHED_CLASS = "text-sys-brand-on-surface-variant";

export function highlightMatchedChars(text: string, indices: number[]): string | JSX.Element {
  if (indices.length === 0 || !text) return text;

  const indexSet = new Set(indices);
  const parts: JSX.Element[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const isMatch = indexSet.has(cursor);
    let end = cursor + 1;
    while (end < text.length && indexSet.has(end) === isMatch) end += 1;
    const slice = text.slice(cursor, end);
    parts.push(
      <span key={cursor} className={isMatch ? GLOBAL_SEARCH_MATCH_CLASS : GLOBAL_SEARCH_UNMATCHED_CLASS}>
        {slice}
      </span>
    );
    cursor = end;
  }

  return <>{parts}</>;
}

export function truncatedSupportingText(text: string | null | undefined): JSX.Element | undefined {
  if (!text) return undefined;
  return <span className="block min-w-0 truncate text-left">{text}</span>;
}

export function personSupportingText(hit: PersonHit): string | undefined {
  return formatPersonSecondaryLine({
    facilityName: hit.facility_name,
    sectionName: hit.section_name,
    otherFacilityCount: hit.other_facility_count
  });
}

export function facilitySupportingText(hit: FacilityHit): string | undefined {
  return formatLocationText(hit.city, hit.state) || hit.section_name || undefined;
}

export function entityHeadline(
  contentHeadline: string | JSX.Element | null | undefined,
  fallback: string,
  query: string
) {
  if (typeof contentHeadline === "string") {
    return highlightMatchedChars(contentHeadline, matchDisplayNameIndices(contentHeadline, query));
  }
  if (contentHeadline) return contentHeadline;
  return highlightMatchedChars(fallback, matchDisplayNameIndices(fallback, query));
}

export function globalSearchPersonListItemProps(hit: PersonHit, query: string): ListItemProps {
  const content = getGlobalHitListItemContent(hit, query);
  return {
    compact: true,
    useAvatar: true,
    centerLeadingIcon: true,
    className: "text-left",
    picture: content?.picture,
    iconName: "person",
    headline: entityHeadline(content?.headline, getGlobalDisplayName(hit), query),
    supportingText: truncatedSupportingText(personSupportingText(hit)),
    longContent: false
  };
}

export function globalSearchFacilityListItemProps(hit: FacilityHit, query: string): ListItemProps {
  const content = getGlobalHitListItemContent(hit, query);
  return {
    compact: true,
    useAvatar: true,
    centerLeadingIcon: true,
    className: "text-left",
    iconName: content?.iconName ?? "store",
    headline: entityHeadline(content?.headline, getGlobalDisplayName(hit), query),
    supportingText: truncatedSupportingText(facilitySupportingText(hit)),
    longContent: false
  };
}
